import { last } from 'lodash/fp';
import { BORDER_RADIUSES, FONT_WEIGHTS } from 'shared/styles/variables';
import styled from 'styled-components';

import IconFile from 'images/icons/file.svg';
import { useButtonSSTStyleContext } from 'inquiry/context/ButtonSSTStyleContext';
import { colorCss } from 'lib/inquiryStyles';
import { ButtonComponentStyles } from 'lib/interfaces/ui-components';
import ThemedSvg from 'verify/components/ThemedSvg';
import { DocumentFileOriginal } from 'verify/models/document-file';

const PreviewButton = styled.button`
  position: relative;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;

  height: 40px;
  width: 40px;
  padding: 2px;
  border-radius: ${BORDER_RADIUSES.rounded};

  flex: none;
  /* Stop flex child from growing out of parent */
  min-height: 0;
`;

const FileCount = styled.div<{ styles?: ButtonComponentStyles }>`
  position: absolute;
  top: -8px;
  right: -8px;

  height: 16px;
  width: 16px;
  border-radius: ${BORDER_RADIUSES.circle};
  background-color: ${({ theme, styles }) =>
    colorCss(styles?.['background-color']?.base?.base) ??
    theme.colorButtonPrimary ??
    theme.colorPrimary};
  color: ${({ theme, styles }) =>
    colorCss(styles?.['text-color']?.base?.base) ?? theme.colorFontButtonPrimary};
  font-size: 10px;
  font-weight: ${FONT_WEIGHTS.medium};
`;

const Thumbnail = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
  image-orientation: from-image;
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const StyledThemedSvg = styled(ThemedSvg)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

function isImage(fileType?: string): boolean {
  return fileType?.includes('image') ?? false;
}

interface PreviewThumbnailProps {
  files: DocumentFileOriginal[];
  onClick?: () => void;
}

const PreviewThumbnail = ({ files = [], onClick }: PreviewThumbnailProps) => {
  const buttonStyles = useButtonSSTStyleContext();

  if (files.length === 0) {
    return null;
  }

  const lastFile = last(files);

  return (
    <PreviewButton
      onClick={onClick}
      id='preview-thumbnail__button'
      data-test='preview-thumbnail__button'
    >
      <FileCount styles={buttonStyles?.primary}>{files.length}</FileCount>

      {lastFile != null && isImage(lastFile['file-type']) ? (
        <Thumbnail src={lastFile.url} data-test='preview-thumbnail__image' />
      ) : (
        <StyledThemedSvg data-test='preview-thumbnail__icon-file'>
          <IconFile />
        </StyledThemedSvg>
      )}
    </PreviewButton>
  );
};

export default PreviewThumbnail;
